import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Flame, Share2, X, Download } from "lucide-react";

/**
 * Focus Streak badge — sits on the dashboard, shows today / week / streak
 * counts pulled from /api/focus/stats. Click to open a share-ready card.
 */
export default function FocusStreak() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/focus/stats");
      setS(r.data);
    } catch (_) { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  if (!s) return null;

  const lit = s.today > 0 || s.streak_days > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hud-frame px-4 py-3 flex items-center gap-4 hover:border-oud transition-colors"
        data-testid="focus-streak-badge"
      >
        <Flame
          size={18}
          className={lit ? "text-oud" : "text-muted-foreground"}
          strokeWidth={1.6}
        />
        <div className="text-left">
          <div className="kpi-label">Focus streak</div>
          <div className="font-display font-bold text-base leading-none mt-1 tracking-tight">
            {s.streak_days > 0
              ? `${s.streak_days}-day streak`
              : s.today > 0
              ? "Streak started today"
              : "No streak yet"}
          </div>
          <div className="mono text-[10px] tracking-widest text-muted-foreground mt-1">
            {s.today} today · {s.week} this week · {s.minutes_week} min
          </div>
        </div>
        <Share2 size={11} className="text-muted-foreground" />
      </button>

      {open && createPortal(
        <ShareCardModal stats={s} onClose={() => setOpen(false)} />,
        document.body
      )}
    </>
  );
}

/* ---------------- Share card modal ---------------- */

function ShareCardModal({ stats, onClose }) {
  const { user } = useAuth();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const name =
    (user?.name || user?.email?.split("@")[0] || "Operator").trim();

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Use html-to-image dynamically — keep it out of the main bundle.
      const mod = await import("html-to-image");
      const png = await mod.toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#080a0e",
      });
      const a = document.createElement("a");
      a.href = png;
      a.download = `focus-streak-${stats.streak_days}d.png`;
      a.click();
    } catch (e) {
      // Fallback: prompt user to screenshot
      // eslint-disable-next-line no-alert
      alert("Download failed — just take a screenshot of the card.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-10 overflow-y-auto"
      onClick={onClose}
      data-testid="streak-share-modal"
    >
      <div
        className="w-full max-w-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Card — this is what gets exported */}
        <div
          ref={cardRef}
          className="relative overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 80% at 10% 0%, hsl(33 55% 18%) 0%, hsl(220 15% 4%) 55%)",
            border: "1px solid hsl(33 55% 35%)",
            padding: "44px 40px",
            aspectRatio: "1.4 / 1",
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <div
                className="font-bold uppercase tracking-tight leading-none"
                style={{
                  fontFamily: "'Chakra Petch', sans-serif",
                  color: "hsl(210 20% 94%)",
                  fontSize: "22px",
                  letterSpacing: "0.02em",
                }}
              >
                Command·<span style={{ color: "hsl(33 55% 58%)" }}>Center</span>
              </div>
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: "italic",
                  color: "hsl(33 40% 75%)",
                  fontSize: "12px",
                  marginTop: "6px",
                }}
              >
                For CEO's Only
              </div>
            </div>
            <Flame
              size={28}
              strokeWidth={1.4}
              style={{ color: "hsl(33 55% 58%)" }}
            />
          </div>

          <div style={{ marginTop: "32px" }}>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "10px",
                letterSpacing: "0.24em",
                color: "hsl(33 40% 75%)",
                textTransform: "uppercase",
              }}
            >
              Focus streak
            </div>
            <div
              style={{
                fontFamily: "'Chakra Petch', sans-serif",
                fontWeight: 700,
                fontSize: "84px",
                lineHeight: "0.92",
                marginTop: "10px",
                color: "hsl(210 20% 94%)",
                letterSpacing: "-0.02em",
              }}
            >
              {stats.streak_days || stats.today}
              <span
                style={{
                  fontSize: "26px",
                  color: "hsl(33 55% 58%)",
                  marginLeft: "8px",
                }}
              >
                {stats.streak_days > 0 ? "days" : stats.today === 1 ? "session" : "sessions"}
              </span>
            </div>
          </div>

          {/* Sub-stats */}
          <div
            style={{
              marginTop: "36px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: "20px",
              paddingTop: "20px",
              borderTop: "1px solid hsl(33 40% 28%)",
            }}
          >
            <Stat label="Today" value={`${stats.today}`} />
            <Stat label="This week" value={`${stats.week}`} />
            <Stat label="Minutes" value={`${stats.minutes_week}`} />
          </div>

          {/* Signature */}
          <div
            style={{
              position: "absolute",
              bottom: "20px",
              right: "40px",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "9px",
              letterSpacing: "0.24em",
              color: "hsl(215 12% 55%)",
              textTransform: "uppercase",
            }}
          >
            {name} · CommandCenterCEO.com
          </div>
        </div>

        {/* Controls (outside the exported card) */}
        <div className="flex items-center justify-between mt-5">
          <button onClick={onClose} className="hud-btn !text-[10px]" data-testid="streak-close">
            <X size={11} /> CLOSE
          </button>
          <button
            onClick={download}
            disabled={downloading}
            className="hud-btn hud-btn-primary !text-[10px]"
            data-testid="streak-download"
          >
            <Download size={11} />
            {downloading ? "EXPORTING…" : "DOWNLOAD PNG"}
          </button>
        </div>
        <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground/70 text-center mt-3">
          Post it · screenshot it · brag a little
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: "9px",
          letterSpacing: "0.24em",
          color: "hsl(33 40% 75%)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "'Chakra Petch', sans-serif",
          fontWeight: 700,
          fontSize: "26px",
          color: "hsl(210 20% 94%)",
          marginTop: "4px",
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </div>
    </div>
  );
}
